import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Clock, Calendar as CalendarIcon,
  Palmtree, ChevronDown, Plus, Trash2, Download,
} from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useEmployeeHoursSummary } from "@/hooks/useEmployeeHoursSummary";
import { downloadEmployeePayslip, getAvailablePayslipMonths } from "@/utils/payslipDownload";

// ─── Sub-components ─────────────────────────────────────────────────

function DiffBadge({ diff }: { diff: number }) {
  if (Math.abs(diff) < 0.01) {
    return <Badge variant="secondary" className="text-xs font-normal">0</Badge>;
  }
  if (diff > 0) {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs font-normal">
        +{diff.toFixed(1)}h
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs font-normal">
      {diff.toFixed(1)}h
    </Badge>
  );
}

function VacationBookingDialog({ onBook, balance }: {
  onBook: (date: Date, hours: number, description?: string) => Promise<void>;
  balance: number;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date>();
  const [hours, setHours] = useState("4");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const reset = () => { setDate(undefined); setHours("4"); setDescription(""); };

  const handleSubmit = async () => {
    if (!date || !hours) return;
    const h = parseFloat(hours);
    if (isNaN(h) || h <= 0) return;

    setSubmitting(true);
    try {
      await onBook(date, h, description || undefined);
      toast({ title: "Ferien gebucht", description: `${h} Std. am ${format(date, "dd.MM.yyyy", { locale: de })}` });
      reset();
      setOpen(false);
    } catch {
      toast({ title: "Fehler", description: "Ferien konnten nicht gebucht werden", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full text-xs">
          <Plus className="h-3 w-3 mr-1" />Ferien buchen
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Ferien buchen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Datum</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "dd.MM.yyyy", { locale: de }) : "Datum wählen"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="pointer-events-auto" />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label>Stunden</Label>
            <Input type="number" step="0.5" min="0.5" max="8" value={hours} onChange={(e) => setHours(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Bemerkung (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="z.B. Arzttermin" />
          </div>
          <p className="text-xs text-muted-foreground">Verfügbar: {balance.toFixed(1)} Std.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting || !date || !hours}>
              {submitting ? "Buche..." : "Buchen"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main component ─────────────────────────────────────────────────

export function EmployeeHoursCard() {
  const currentYear = new Date().getFullYear();
  const [monthsOpen, setMonthsOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const { toast } = useToast();

  const isViewingCurrentYear = selectedYear === currentYear;
  const availableYears = Array.from({ length: currentYear - 2024 }, (_, i) => 2025 + i);

  const summary = useEmployeeHoursSummary(selectedYear);
  const {
    totalActualHours,
    totalExpectedHours,
    hoursDifference,
    hoursCarryover,
    totalBalance,
    vacationEntitlement,
    vacationCarryover,
    vacationTaken,
    vacationBalance,
    vacationAccrued,
    monthlyBreakdown,
    vacationEntries,
    monthlyTarget,
    isLoading,
    hasEmployeeRecord,
    employeeId,
    contractStartDate,
    bookVacation,
    deleteVacation,
  } = summary;

  // ── Payslip download state ──
  const [payslipMonths, setPayslipMonths] = useState<Array<{month: number, year: number}>>([]);
  const [payslipSelection, setPayslipSelection] = useState<string>("");
  const [payslipDownloading, setPayslipDownloading] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    getAvailablePayslipMonths(employeeId).then(setPayslipMonths);
  }, [employeeId]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteVacation(id);
      toast({ title: "Gelöscht", description: "Ferien-Buchung wurde gelöscht." });
    } catch {
      toast({ title: "Fehler", description: "Löschen fehlgeschlagen", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const handlePayslipDownload = async () => {
    if (!payslipSelection || !employeeId || payslipDownloading) return;
    const [year, month] = payslipSelection.split("-").map(Number);
    setPayslipDownloading(true);
    try {
      await downloadEmployeePayslip(employeeId, month, year);
      toast({ title: "Download erfolgreich", description: "Lohnabrechnung wurde heruntergeladen" });
    } catch {
      toast({
        title: "Download fehlgeschlagen",
        description: "Fehler beim Herunterladen der Lohnabrechnung. Bitte versuche es erneut.",
        variant: "destructive",
      });
    } finally {
      setPayslipDownloading(false);
    }
  };

  const today = format(new Date(), "yyyy-MM-dd");
  const contractLabel = contractStartDate
    ? `ab ${format(contractStartDate, "MMMM yyyy", { locale: de })}`
    : "";

  if (!hasEmployeeRecord) return null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-8 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-20 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalVacationPool = vacationEntitlement + vacationCarryover;
  const takenPct = totalVacationPool > 0 ? Math.min((vacationTaken / totalVacationPool) * 100, 100) : 0;
  const accrualDelta = vacationAccrued - vacationTaken;

  return (
    <Card className="sticky top-4">
      {/* ── Header with year selector ── */}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-primary" />
            Arbeitsübersicht
          </CardTitle>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="h-7 w-[85px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {contractLabel && (
            <p className="text-xs text-muted-foreground">{contractLabel}</p>
          )}
          {monthlyTarget > 0 && (
            <Badge variant="secondary" className="text-[10px] font-normal">
              Pensum: {monthlyTarget} Std./Mt.
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* ════ SECTION: Arbeitsstunden ════ */}
        <div>
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Arbeitsstunden {selectedYear}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-blue-50 rounded-lg">
              <div className="text-lg font-bold text-blue-700">{totalActualHours.toFixed(1)}</div>
              <div className="text-[10px] text-muted-foreground leading-tight">Ist (geleistet)</div>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <div className="text-lg font-bold text-gray-600">{totalExpectedHours.toFixed(1)}</div>
              <div className="text-[10px] text-muted-foreground leading-tight">Soll (erwartet)</div>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <div className={`text-lg font-bold ${hoursDifference >= 0 ? "text-green-600" : "text-red-600"}`}>
                {hoursDifference >= 0 ? "+" : ""}{hoursDifference.toFixed(1)}
              </div>
              <div className="text-[10px] text-muted-foreground leading-tight">+/- Stunden</div>
            </div>
          </div>

          {/* Hours carryover from previous year(s) */}
          {hoursCarryover !== 0 && (
            <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground bg-slate-50 rounded px-2 py-1">
              <span>Übertrag bis {selectedYear - 1}</span>
              <span className={`font-semibold ${hoursCarryover >= 0 ? "text-green-600" : "text-red-600"}`}>
                {hoursCarryover >= 0 ? "+" : ""}{hoursCarryover.toFixed(1)}h
              </span>
            </div>
          )}
          {hoursCarryover !== 0 && (
            <div className="flex items-center justify-between text-[10px] bg-slate-50 rounded px-2 py-1 font-semibold">
              <span>Gesamtsaldo</span>
              <span className={totalBalance >= 0 ? "text-green-600" : "text-red-600"}>
                {totalBalance >= 0 ? "+" : ""}{totalBalance.toFixed(1)}h
              </span>
            </div>
          )}
        </div>

        {/* ════ SECTION: Ferienkontingent ════ */}
        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <Palmtree className="h-4 w-4 text-green-600" />
            Ferienkontingent {selectedYear}
          </div>

          {/* Vacation grid */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-sm font-semibold">{vacationEntitlement.toFixed(1)}h</div>
              <div className="text-[10px] text-muted-foreground">Kontingent</div>
            </div>
            <div>
              <div className="text-sm font-semibold text-orange-600">{vacationTaken.toFixed(1)}h</div>
              <div className="text-[10px] text-muted-foreground">Bezogen</div>
            </div>
            <div>
              <div className={`text-sm font-semibold ${vacationBalance >= 0 ? "text-green-600" : "text-red-600"}`}>
                {vacationBalance.toFixed(1)}h
              </div>
              <div className="text-[10px] text-muted-foreground">Verfügbar</div>
            </div>
          </div>

          {/* Carryover from previous year */}
          {vacationCarryover > 0 && (
            <div className="flex items-center justify-between text-[10px] text-muted-foreground bg-blue-50 rounded px-2 py-1">
              <span>Übertrag {selectedYear - 1}</span>
              <span className="font-semibold text-blue-600">+{vacationCarryover.toFixed(1)}h</span>
            </div>
          )}

          {/* Progress bar: taken vs total pool (entitlement + carryover) */}
          <div className="space-y-1">
            <div className="h-2 rounded-full bg-muted overflow-hidden flex">
              <div className="bg-orange-400 h-full transition-all" style={{ width: `${takenPct}%` }} />
              <div className="bg-green-400/40 h-full transition-all" style={{ width: `${100 - takenPct}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>
                {contractStartDate && selectedYear === contractStartDate.getFullYear()
                  ? `anteilig ${contractLabel}`
                  : `${totalVacationPool.toFixed(0)} Std. gesamt`}
              </span>
            </div>
          </div>

          {/* Accrual line — only for current year */}
          {isViewingCurrentYear && (
            <p className="text-[10px] text-muted-foreground text-center" title="Anteiliger Anspruch bis heute vs. bezogene Ferien">
              Aufgelaufen: {vacationAccrued.toFixed(1)}h · Delta: {accrualDelta >= 0 ? "+" : ""}{accrualDelta.toFixed(1)}h
            </p>
          )}

          {/* Vacation entries list */}
          {vacationEntries.length > 0 && (
            <div className="pt-1 space-y-1">
              {vacationEntries.map((e) => {
                const isFuture = e.date >= today;
                return (
                  <div key={e.id} className="flex items-center justify-between text-[10px] text-muted-foreground gap-1">
                    <span>{format(new Date(e.date), "dd.MM.yyyy", { locale: de })}</span>
                    <span className="flex items-center gap-1">
                      {e.hours}h{e.description ? ` – ${e.description}` : ""}
                      {isFuture && isViewingCurrentYear && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" disabled={deletingId === e.id}>
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Ferien-Buchung löschen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {e.hours}h am {format(new Date(e.date), "dd.MM.yyyy", { locale: de })} wird unwiderruflich gelöscht.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(e.id)}>Löschen</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {isViewingCurrentYear && <VacationBookingDialog onBook={bookVacation} balance={vacationBalance} />}
        </div>

        {/* ════ SECTION: Monatliche Übersicht (Collapsible) ════ */}
        <Collapsible open={monthsOpen} onOpenChange={setMonthsOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium hover:text-primary transition-colors">
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4 text-primary" />
              Monatliche Übersicht
            </div>
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", monthsOpen && "rotate-180")} />
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 space-y-1.5">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 text-[10px] text-muted-foreground font-medium pb-1 border-b">
              <div>Monat</div>
              <div className="text-right w-12">Geleistet</div>
              <div className="text-right w-12">Erwartet</div>
              <div className="text-right w-14">+/-</div>
            </div>

            <TooltipProvider>
              {monthlyBreakdown.map((month) => {
                const hasHolidays = month.holidays.length > 0;
                const hasClosures = month.closureDays > 0;
                const hasVacation = month.vacationHours > 0;

                return (
                  <div
                    key={month.monthKey}
                    className={`grid grid-cols-[1fr_auto_auto_auto] gap-x-2 items-center text-xs py-1 ${
                      month.isUnpaidLeave ? "bg-amber-50 rounded px-1 -mx-1" :
                      hasHolidays || hasClosures ? "bg-blue-50 rounded px-1 -mx-1" :
                      hasVacation ? "bg-green-50 rounded px-1 -mx-1" : ""
                    }`}
                  >
                    <div className="flex items-center gap-1 flex-wrap">
                      <span>{month.monthLabel}</span>
                      {month.isUnpaidLeave && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 text-amber-700 border-amber-300">
                          Unbezahlter Urlaub
                        </Badge>
                      )}
                      {hasHolidays && !month.isUnpaidLeave && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="text-[9px] px-1 py-0 text-blue-600 border-blue-300 cursor-help">
                              {month.holidays.length} Feiertag{month.holidays.length > 1 ? "e" : ""}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {month.holidays.map((h) => (
                              <div key={h.date}>{h.name} ({h.date})</div>
                            ))}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {hasClosures && !month.isUnpaidLeave && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 text-purple-600 border-purple-300">
                          Betriebsferien ({month.closureDays}d)
                        </Badge>
                      )}
                      {hasVacation && !month.isUnpaidLeave && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 text-green-700 border-green-300">
                          {month.vacationHours.toFixed(1)}h Ferien
                        </Badge>
                      )}
                    </div>
                    <div className="text-right w-12 font-mono">{month.actualHours.toFixed(1)}</div>
                    <div className="text-right w-12 font-mono text-muted-foreground">{month.expectedHours.toFixed(1)}</div>
                    <div className="text-right w-14"><DiffBadge diff={month.difference} /></div>
                  </div>
                );
              })}
            </TooltipProvider>

            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 items-center text-xs py-1.5 border-t font-semibold">
              <div>Gesamt {selectedYear}</div>
              <div className="text-right w-12 font-mono">{totalActualHours.toFixed(1)}</div>
              <div className="text-right w-12 font-mono text-muted-foreground">{totalExpectedHours.toFixed(1)}</div>
              <div className="text-right w-14"><DiffBadge diff={hoursDifference} /></div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* ════ SECTION: Lohnabrechnungen (Payslip Download) ════ */}
        {payslipMonths.length > 0 && (
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <Download className="h-4 w-4 text-primary" />
              Lohnabrechnungen
            </div>

            <Select value={payslipSelection} onValueChange={setPayslipSelection}>
              <SelectTrigger className="text-xs">
                <SelectValue placeholder="Monat auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {payslipMonths.map(({ month, year }) => (
                  <SelectItem key={`${year}-${month}`} value={`${year}-${month}`}>
                    {format(new Date(year, month - 1), "MMMM yyyy", { locale: de })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              size="sm"
              className="w-full text-xs"
              onClick={handlePayslipDownload}
              disabled={!payslipSelection || payslipDownloading}
            >
              {payslipDownloading ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
                  Herunterladen...
                </>
              ) : (
                <>
                  <Download className="h-3 w-3 mr-1" />
                  Herunterladen
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
